import { Link, useSearch } from "@tanstack/react-router"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useFormContext } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"
import { SearchSchema as QRSearchSchema } from "routes/_base/_auth/settings/channels/qr/route"
import { z } from "zod"
import { DownloadQRImage } from "../../../Sections/triggers/qr/DownloadQRImage"
import QRFormSelector from "../../../Sections/triggers/qr/Form/QRFormSelector"

type Props = {
  isDisabled: boolean
}

export function QRMetadataForm({ isDisabled }: Props) {
  const { t } = useTranslation()
  const form = useFormContext<z.infer<typeof UpdateCampaignSchemaFrontend>>()
  const { data: employee } = useAuthEmployeeQuery()
  const search = useSearch({ strict: false })
  const triggerIntegration = form.watch("triggerIntegration")
  const qrId = triggerIntegration?.type === "qr" ? triggerIntegration.qrId : undefined

  return (
    <>
      <span className="text-sm text-muted-foreground">
        <Trans
          i18nKey={"campaigns.trigger.metadata.qr.description"}
          components={{
            a: (
              <Link
                className="underline text-muted-foreground"
                to="/settings/channels/qr"
                search={QRSearchSchema.parse(search)}
                target="_blank"
              />
            ),
          }}
        />
      </span>
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => (
          <FormItem className="flex flex-col gap-2">
            <FormLabel>{t("campaigns.trigger.metadata.qr.label")}</FormLabel>
            <FormControl>
              <QRFormSelector
                value={qrId}
                emptyPlaceholder={t("campaigns.trigger.metadata.qr.emptyPlaceholder")}
                placeholder={t("campaigns.trigger.metadata.qr.placeholder")}
                searchPlaceholder={t("campaigns.trigger.metadata.qr.searchPlaceholder")}
                onChange={(qr) => {
                  field.onChange(qr ? { type: "qr", qrId: qr.value } : null, {
                    shouldDirty: true,
                  })
                }}
                hasCreateNew
                isDisabled={isDisabled}
              />
            </FormControl>
            <FormMessage />
            {qrId && (
              <DownloadQRImage qrId={qrId} companyId={employee?.companyId} className="w-fit" />
            )}
          </FormItem>
        )}
      />
    </>
  )
}
